import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IListaCidades } from '../../../models';
import { IFormError, IFormProps } from '../../utils';

interface ICreateListaCidadesFormFields {
    nome: string;
    idUsuario: number;
    // idCliente: number;
}

interface IEditListaCidadesFormFields {
    idVelesGeoListaCidades: number;
    nome: string;
    cidadeUnitaria?: string;
    estadoUnitario?: string;
    cidades: string[]
    ativa: boolean;
}

const createListaCidadesFormFieldsInitialState: ICreateListaCidadesFormFields = {
    nome: '',
    idUsuario: 0,
    // idCliente: 0
}

const editListaCidadesFormFieldsInitialState: IEditListaCidadesFormFields = {
    idVelesGeoListaCidades: 0,
    nome: '',
    cidadeUnitaria: '',
    estadoUnitario: '',
    ativa: false,
    cidades: []
}

interface IListaCidadesState {
    matchCode: {
        selectedColumn: string,
        distinctValues: number,
        validValues: number
    },
    fileData: {
        name: string,
        size: string,
        columns: string[],
        type: string,
        data: any[],
        validData?: any[],
        invalidData?: any[]
    },
    searchTerm: string
    listasCidades: IListaCidades[]
    selectedCidadeList?: IListaCidades
    fileListData: {
        fileListData: string[]
        fileListTableValues: {
            fileListValidData: string[]
            fileListInvalidData: string[]
            fileListRepeatedData: string[]
        }
    }
    createListaCidadesForm: IFormProps<ICreateListaCidadesFormFields>
    openCreateListaCidades: boolean
    editListaCidadesForm: IFormProps<IEditListaCidadesFormFields>
    openEditListaCidades: boolean
    openImportFileListaCidades: boolean
    openPreviewListaCidades: boolean
    openEditCidades: boolean
}

const initialState: IListaCidadesState = {
    fileListData: {
        fileListData: [],
        fileListTableValues: {
            fileListInvalidData: [],
            fileListRepeatedData: [],
            fileListValidData: []
        }
    },
    matchCode: {
        selectedColumn: '',
        distinctValues: 0,
        validValues: 0
    },
    fileData: {
        name: '',
        size: '',
        columns: [],
        type: '',
        data: []
    },
    listasCidades: [],
    searchTerm: '',
    createListaCidadesForm: {
        errors: [],
        fields: createListaCidadesFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openCreateListaCidades: false,
    editListaCidadesForm: {
        errors: [],
        fields: editListaCidadesFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openEditListaCidades: false,
    openImportFileListaCidades: false,
    openPreviewListaCidades: false,
    openEditCidades: false
};

export const validateCreateListaCidadesForm = (fields: ICreateListaCidadesFormFields): IFormError<keyof ICreateListaCidadesFormFields>[] => {
    const errors: IFormError<keyof ICreateListaCidadesFormFields>[] = [];

    if (!fields.nome) {
        errors.push({
            key: 'nome',
            message: 'Nome é obrigatório',
        })
    } else {
        if (fields.nome.length === 0 || fields.nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'nome',
                message: 'O nome da lista não pode ficar em branco'
            })
        }
    }

    return errors;
}

export const validateEditListaCidadesForm = (fields: IEditListaCidadesFormFields): IFormError<keyof IEditListaCidadesFormFields>[] => {
    const errors: IFormError<keyof IEditListaCidadesFormFields>[] = [];

    if (!fields.nome) {
        errors.push({
            key: 'nome',
            message: 'Nome é obrigatório',
        });
    } else {
        if (fields.nome.length === 0 || fields.nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'nome',
                message: 'O nome do projeto não pode ficar em branco'
            })
        }
    }

    return errors;
}

const listaCidadesSlice = createSlice({
    name: 'listaCidades',
    initialState,
    reducers: {
        resetListaCidadesState(state) {
            state = initialState
        },
        setListasCidadesState(state, action: PayloadAction<{ listasCidades: IListaCidades[] }>) {
            state.listasCidades = action.payload.listasCidades
        },
        resetListasCidadesState(state) {
            state.listasCidades = initialState.listasCidades
        },
        setSelectedListasCidadesState(state, action: PayloadAction<{ listasCidades: IListaCidades }>) {
            state.selectedCidadeList = action.payload.listasCidades
        },
        // Create Lista Cidades
        setCreateListaCidadesNomeState(state, action: PayloadAction<{ nome: string }>) {
            state.createListaCidadesForm.fields.nome = action.payload.nome
        },
        setCreateListaCidadesIdUsuarioState(state, action: PayloadAction<{ idUsuario: number }>) {
            state.createListaCidadesForm.fields.idUsuario = action.payload.idUsuario
        },
        setCreateListaCidadesFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.createListaCidadesForm.touched = action.payload.touched;
        },
        setCreateListaCidadesFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateListaCidadesFormFields>[] }>) {
            state.createListaCidadesForm.errors = action.payload.errors;
        },
        setCreateListaCidadesFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.createListaCidadesForm.delivered = action.payload.delivered;
        },
        resetCreateListaCidadesFormState(state) {
            state.createListaCidadesForm = initialState.createListaCidadesForm
        },
        // Edit Lista Cidades
        setEditListaCidadesIdVelesGeoListaCidadesState(state, action: PayloadAction<{ idVelesGeoListaCidades: number }>) {
            state.editListaCidadesForm.fields.idVelesGeoListaCidades = action.payload.idVelesGeoListaCidades
        },
        setEditListaCidadesNomeState(state, action: PayloadAction<{ nome: string }>) {
            state.editListaCidadesForm.fields.nome = action.payload.nome
        },
        setEditListaCidadesAddCidadeUnitariaState(state, action: PayloadAction<{ cidadeUnitaria: string }>) {
            state.editListaCidadesForm.fields.cidadeUnitaria = action.payload.cidadeUnitaria;
        },
        setEditListaCidadesSelectEstadoUnitarioState(state, action: PayloadAction<{ estadoUnitario: string }>) {
            state.editListaCidadesForm.fields.estadoUnitario = action.payload.estadoUnitario;
        },
        setEditListaCidadesAtivaState(state, action: PayloadAction<{ ativa: boolean }>) {
            state.editListaCidadesForm.fields.ativa = action.payload.ativa
        },
        setCidadesEditListaCidadesState(state, action: PayloadAction<{ cidades: string[] }>) {
            state.editListaCidadesForm.fields.cidades = action.payload.cidades
        },
        setAddCidadeListEditListaCidadesState(state, action: PayloadAction<{ cidades: string[] }>) {
            state.editListaCidadesForm.fields.cidades = [...state.editListaCidadesForm.fields.cidades, ...action.payload.cidades]
        },
        setEditListaCidadeAddCidadeUnitariaState(state, action: PayloadAction<{ cidade: string, estado: string }>) {
            const cidadeUnitaria = action.payload.cidade + '/' + action.payload.estado;       
            state.editListaCidadesForm.fields.cidades.push(cidadeUnitaria)
        },
        setEditListaCidadeAddCidadesState(state, action: PayloadAction<{ cidade: string }>) {
            if (state.editListaCidadesForm.fields.cidades.includes(action.payload.cidade)) {
                state.editListaCidadesForm.fields.cidades.push(action.payload.cidade);
            }
            state.editListaCidadesForm.fields.cidades.push(action.payload.cidade)
        },
        setEditListaCidadesRemoveCidadesState(state, action: PayloadAction<{ cidade: string }>) {
            state.editListaCidadesForm.fields.cidades = state.editListaCidadesForm.fields.cidades.filter(item => item !== action.payload.cidade)
        },
        setEditListaCidadesEditCidadesState(state, action: PayloadAction<{
            oldCidade: string,
            newCidade: string
        }>) {
            if (state.editListaCidadesForm.fields.cidades.includes(action.payload.newCidade)) {
                return
            }

            state.editListaCidadesForm.fields.cidades = state.editListaCidadesForm.fields.cidades.map(item => item === action.payload.oldCidade ? action.payload.newCidade : item)
        },
        setEditListaCidadesFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.editListaCidadesForm.touched = action.payload.touched;
        },
        setEditListaCidadesFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof IEditListaCidadesFormFields>[] }>) {
            state.editListaCidadesForm.errors = action.payload.errors;
        },
        setEditListaCidadesFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.editListaCidadesForm.delivered = action.payload.delivered;
        },
        resetEditListaCidadesFormState(state) {
            state.editListaCidadesForm = initialState.editListaCidadesForm
        },
        setListaCidadesFileDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListData = action.payload.value
        },
        setListaCidadesFileValidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListValidData = action.payload.value
        },
        setListaCidadesFileRepeatedDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListRepeatedData = action.payload.value
        },
        setListaCidadesFileInvalidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListInvalidData = action.payload.value
        },
        setListaCidadesSelectedColumnState(state, action: PayloadAction<{
            selectedColumn: string
        }>) {
            state.matchCode.selectedColumn = action.payload.selectedColumn
        },
        setListaCidadesDistinctValuesState(state, action: PayloadAction<{
            distinctValues: number
        }>) {
            state.matchCode.distinctValues = action.payload.distinctValues
        },
        setListaCidadesValidValuesState(state, action: PayloadAction<{
            validValues: number
        }>) {
            state.matchCode.validValues = action.payload.validValues
        },
        setListaCidadesFileDataState(state, action: PayloadAction<{
            fileData: {
                name: string,
                size: string,
                columns: string[],
                type: string,
                data: any[],
                validData?: any[],
                invalidData?: any[]
            }
        }>) {

            state.fileData.name = action.payload.fileData.name
            state.fileData.size = action.payload.fileData.size
            state.fileData.columns = action.payload.fileData.columns
            state.fileData.data = action.payload.fileData.data
        },
        resetListaCidadesFileDataState(state) {
            state.fileData = initialState.fileData
            state.matchCode = initialState.matchCode
            state.fileListData = initialState.fileListData
        },
        setOpenCreateListaCidadesState(state, action: PayloadAction<boolean>) {
            state.openCreateListaCidades = action.payload
        },
        setOpenEditListaCidadesState(state, action: PayloadAction<boolean>) {
            state.openEditListaCidades = action.payload
        },
        setOpenImportFileCidadesState(state, action: PayloadAction<boolean>) {
            state.openImportFileListaCidades = action.payload
        },
        setOpenPreviewListaCidadesState(state, action: PayloadAction<boolean>) {
            state.openPreviewListaCidades = action.payload
        },
        setOpenEditCidadesState(state, action: PayloadAction<boolean>) {
            state.openEditCidades = action.payload
        },
        setEditListaCidadesRemoveCidadeState(state, action: PayloadAction<{ cidade: string }>) {
            state.editListaCidadesForm.fields.cidades = state.editListaCidadesForm.fields.cidades.filter(item => item !== action.payload.cidade)
        }
    }
});

export const {
    resetCreateListaCidadesFormState,
    resetEditListaCidadesFormState,
    resetListasCidadesState,
    resetListaCidadesFileDataState,
    resetListaCidadesState,
    setAddCidadeListEditListaCidadesState,
    setCidadesEditListaCidadesState,
    setCreateListaCidadesFormDeliveredState,
    setCreateListaCidadesFormErrorsState,
    setCreateListaCidadesFormTouchedState,
    setCreateListaCidadesIdUsuarioState,
    setCreateListaCidadesNomeState,
    setEditListaCidadeAddCidadesState,
    setEditListaCidadesAtivaState,
    setEditListaCidadesEditCidadesState,
    setEditListaCidadesFormDeliveredState,
    setEditListaCidadesFormErrorsState,
    setEditListaCidadesFormTouchedState,
    setEditListaCidadesIdVelesGeoListaCidadesState,
    setEditListaCidadesNomeState,
    setEditListaCidadesAddCidadeUnitariaState,
    setEditListaCidadesSelectEstadoUnitarioState,
    setEditListaCidadeAddCidadeUnitariaState,
    setEditListaCidadesRemoveCidadesState,
    setListaCidadesDistinctValuesState,
    setListaCidadesFileDataListState,
    setListaCidadesFileDataState,
    setListaCidadesFileInvalidDataListState,
    setListaCidadesFileRepeatedDataListState,
    setListaCidadesFileValidDataListState,
    setListaCidadesSelectedColumnState,
    setListaCidadesValidValuesState,
    setListasCidadesState,
    setSelectedListasCidadesState,
    setOpenCreateListaCidadesState,
    setOpenEditListaCidadesState,
    setOpenImportFileCidadesState,
    setOpenPreviewListaCidadesState,
    setOpenEditCidadesState,
    setEditListaCidadesRemoveCidadeState
} = listaCidadesSlice.actions;

export const listaCidadesReducer = listaCidadesSlice.reducer;